import { ReportProcessorNotification, plannerEvent } from  "@/interfaces/planner";
import React, { createContext, useState, useEffect, useMemo, useRef } from "react";

import { daySpan } from  '@/helpers/Planner';
import useWeekBuilder, { WeekBuilder } from "@/components/hooks/useWeekBuilder";
import useHTTP from "@/components/hooks/useHTTP";
import { Icalendar } from "@/components/page/Planner";
import useSignalR from "@/components/hooks/useSignalR";
import { HubConnection } from "@microsoft/signalr";
import { formatDateStringWithDateTimeOffset } from  "@/helpers/Dates";

export interface IPlannerContext {
    events: plannerEvent[],
    setEvents: (events: plannerEvent[]) => void
    formatEvents: (events: plannerEvent[]) => plannerEvent[]
    getEvents: (id:number) => void
    weekBuilder?: WeekBuilder
    selectedCal: Icalendar,
    setSelectedCal: (cal:Icalendar) => void,
    updateFilters: (filters: PlannerFilters) => void,
    filterResults: PlannerFilters,
    minimumEventTime: number,
    approveAwaiting: (workflowId:number, responseId: number, status: 'approved' | 'rejected') => Promise<void>
    calendarMessage: {timestamp: number},
}

const plannerDefaults:IPlannerContext = {
    events: [],
    setEvents: () => {},
    getEvents: () => {},
    formatEvents:() => [],
    selectedCal:{value:0, label:"", hexCode:""},
    setSelectedCal: () => [],
    updateFilters: (filters: PlannerFilters) => {},
    filterResults: {showActive: true, showCancelled: true},
    minimumEventTime: 15,
    approveAwaiting: (workflowId:number, responseId: number, status: 'approved' | 'rejected') => Promise.resolve(),
    calendarMessage: {timestamp: 0},
}

interface PlannerFilters {
    showActive: boolean,
    showCancelled: boolean
}

export const PlannerContext = createContext(plannerDefaults);

const Get = ({ minimumEventTime=15, children }:{minimumEventTime?: number, children:React.ReactNode}) => {

    const [events, setEvents] = useState<plannerEvent[]>([]);
    
    const [selectedCal, setSelectedCal] = useState<Icalendar>(plannerDefaults.selectedCal);

    const [filterResults, setFilterResults] = useState<PlannerFilters>({showActive: true, showCancelled: true})

    const weekBuilder = useWeekBuilder();

    const {get, put} = useHTTP();

    // signal R stuffs 
    const [calendarMessage, setCalendarMessage] = useState<any>({timestamp: 0});

    const calendarGroupsRef = useRef<number>(0);


    useEffect(() => {
        calendarGroupsRef.current = selectedCal.value;
    },[selectedCal.value]);



   useEffect(() => {
    
        const keepWarm= setInterval(() => {
            setCalendarMessage({timestamp: new Date().toString()})
        }, 30000)

        return () => {clearInterval(keepWarm)}
   }, [])

    // clear events when changing calendar
    useEffect(() => {
        setEvents([]);
    },[selectedCal.value])


    async function getEvents(id:number){

        if (!weekBuilder.firstDay || !weekBuilder.lastDay || !id ) return 

       
        const { data } = await get(`planner/calendar/${id}/events/week?DateWithinPeriod=${encodeURIComponent(formatDateStringWithDateTimeOffset(weekBuilder.firstDay.toDateString()))}`)
            // Parse dates
        if (data.items){
            const eventsWithDates:plannerEvent[] = formatEvents(data.items)
            const events = await getInitialEventNumbers(id, eventsWithDates)

            setEvents(events)
        } 
    }

    async function getInitialEventNumbers(id: number, events: plannerEvent[]){
        const firstDay = new Date(weekBuilder.firstDay).toISOString()
        const lastDay = new Date(weekBuilder.lastDay)
        lastDay.setHours(23)
        lastDay.setMinutes(59)
        
        const { data } = await get(`planner/calendar/${selectedCal.value}/eventsreplies/workflowresponses/approvalcount?start=${firstDay}&end=${lastDay.toISOString()}` )
            .catch(() => {
                return {data: {items: undefined}}
            });

        if (data?.length > 0) {
            return events.map((ev) => {
                let count = data.find(el => el.eventId === ev.id);
                return {...ev, awaitingApprovalCount: count ? count.awaitingApprovalCount : 0}
            })
        }

        return events;
                
         
    }

    async function approveAwaiting(workflowId:number, responseId: number, status: 'approved' | 'rejected'){
        return put(`workflows/${workflowId}/responses/${responseId}`, {status})
    }

    function formatEvents(events: plannerEvent[]):plannerEvent[] {
        return events.map(x => {
            return {
                ...x,
                occurrence: {
                    start: new Date(x.occurrence.start),
                    end: new Date(x.occurrence.end),
                    occurrenceType: x.occurrence.occurrenceType,
                    daySpan: daySpan(x.occurrence.start, x.occurrence.end)
                }
            }
        })
    }

    function updateFilters(filters:PlannerFilters) {
        setFilterResults(filters);
    }

    function applyFiltersResults(evt: plannerEvent) {
        if (filterResults.showActive && filterResults.showCancelled) {
            return true
        }
        if(filterResults.showActive) {
            return !evt.isCancelled
        }
        if(filterResults.showCancelled) {        
            return !!evt.isCancelled
        }
        return false;
    }

    const context:IPlannerContext = useMemo(() => ({
        events: [...events.filter(applyFiltersResults)],
        setEvents: setEvents,
        getEvents: getEvents,
        formatEvents: formatEvents,
        weekBuilder: weekBuilder,
        selectedCal: selectedCal,
        setSelectedCal: setSelectedCal,
        updateFilters: updateFilters,
        filterResults: filterResults,
        minimumEventTime,
        approveAwaiting,
        calendarMessage,
    }), 
    [events, weekBuilder, selectedCal, filterResults, minimumEventTime])

    return (
        <PlannerContext.Provider value={context}>
            {children}
        </PlannerContext.Provider>
    )
}

export default Get;
